// /admin/preview — a three-column directory of all users by kind.
// Each row has a "View dashboard" button that jumps to /admin/preview/<kind>/<id>.
// This is the picker for the read-only mirror pages.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminNav } from '../components/AdminNav';

interface ResidentRow {
  id: number; first_name: string; last_name: string;
  city: string; status: string; archived_at: string | null;
}
interface VolunteerRow {
  id: number; first_name: string; last_name: string;
  city: string | null; status: string; archived_at: string | null;
}
interface ProviderRow {
  id: number; business_name: string; owner_name: string;
  status: string; archived_at: string | null;
}

export function AdminPreviewListPage() {
  const [residents, setResidents] = useState<ResidentRow[]>([]);
  const [volunteers, setVolunteers] = useState<VolunteerRow[]>([]);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [rR, vR, pR] = await Promise.all([
          fetch('/api/admin/residents',  { credentials: 'include' }),
          fetch('/api/admin/volunteers', { credentials: 'include' }),
          fetch('/api/admin/providers',  { credentials: 'include' }),
        ]);
        const rJ = await rR.json() as { ok: boolean; data?: ResidentRow[];   error?: string };
        const vJ = await vR.json() as { ok: boolean; data?: { volunteers: VolunteerRow[] }; error?: string };
        const pJ = await pR.json() as { ok: boolean; data?: ProviderRow[];   error?: string };

        if (!rJ.ok) throw new Error(rJ.error || 'residents fetch failed');
        if (!vJ.ok) throw new Error(vJ.error || 'volunteers fetch failed');
        if (!pJ.ok) throw new Error(pJ.error || 'providers fetch failed');

        setResidents(rJ.data || []);
        setVolunteers(vJ.data?.volunteers || []);
        setProviders(pJ.data || []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Load failed');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ql = q.toLowerCase();
  const matchesR = (r: ResidentRow) =>
    (!ql || `${r.first_name} ${r.last_name} ${r.city}`.toLowerCase().includes(ql)) &&
    (showArchived || !r.archived_at);
  const matchesV = (v: VolunteerRow) =>
    (!ql || `${v.first_name} ${v.last_name} ${v.city || ''}`.toLowerCase().includes(ql)) &&
    (showArchived || !v.archived_at);
  const matchesP = (p: ProviderRow) =>
    (!ql || `${p.business_name} ${p.owner_name}`.toLowerCase().includes(ql)) &&
    (showArchived || !p.archived_at);

  return (
    <>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Admin · Preview</div>
        <h1 className="text-4xl text-forest mb-2 font-display" style={{ fontVariationSettings: '"opsz" 144' }}>
          View as a user.
        </h1>
        <p className="text-forest/70 mb-6">
          Read-only mirror of what each user sees on their dashboard. Pick anyone below.
        </p>

        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or city..."
            className="border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta w-72"
          />
          <label className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-forest/70 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-terracotta"
            />
            Show archived
          </label>
        </div>

        {error && <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm mb-6">{error}</div>}
        {loading ? (
          <div className="text-forest/60 italic">Loading...</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Residents */}
            <section>
              <h2 className="text-xs tracking-[0.25em] uppercase text-forest/60 mb-3">Residents ({residents.filter(matchesR).length})</h2>
              <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
                {residents.filter(matchesR).map((r) => (
                  <Link
                    key={r.id}
                    to={`/admin/preview/resident/${r.id}`}
                    className="block border border-forest/15 bg-cream hover:border-terracotta p-3 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-forest">{r.first_name} {r.last_name}</span>
                      <span className="text-xs text-forest/50">#{r.id}</span>
                      <span className="ml-auto text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/70 px-1.5 py-0.5">{r.status}</span>
                    </div>
                    <div className="text-xs text-forest/65 mt-0.5">{r.city || '—'}</div>
                  </Link>
                ))}
                {residents.filter(matchesR).length === 0 && (
                  <div className="text-sm text-forest/50 italic">No residents.</div>
                )}
              </div>
            </section>

            {/* Volunteers */}
            <section>
              <h2 className="text-xs tracking-[0.25em] uppercase text-forest/60 mb-3">Volunteer Mowkins ({volunteers.filter(matchesV).length})</h2>
              <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
                {volunteers.filter(matchesV).map((v) => (
                  <Link
                    key={v.id}
                    to={`/admin/preview/volunteer/${v.id}`}
                    className="block border border-forest/15 bg-cream hover:border-terracotta p-3 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-forest">{v.first_name} {v.last_name}</span>
                      <span className="text-xs text-forest/50">#{v.id}</span>
                      <span className="ml-auto text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/70 px-1.5 py-0.5">{v.status}</span>
                    </div>
                    <div className="text-xs text-forest/65 mt-0.5">{v.city || '—'}</div>
                  </Link>
                ))}
                {volunteers.filter(matchesV).length === 0 && (
                  <div className="text-sm text-forest/50 italic">No volunteers.</div>
                )}
              </div>
            </section>

            {/* Providers */}
            <section>
              <h2 className="text-xs tracking-[0.25em] uppercase text-forest/60 mb-3">Pro Mowkins ({providers.filter(matchesP).length})</h2>
              <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
                {providers.filter(matchesP).map((p) => (
                  <Link
                    key={p.id}
                    to={`/admin/preview/provider/${p.id}`}
                    className="block border border-forest/15 bg-cream hover:border-terracotta p-3 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-forest">{p.business_name}</span>
                      <span className="text-xs text-forest/50">#{p.id}</span>
                      <span className="ml-auto text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/70 px-1.5 py-0.5">{p.status}</span>
                    </div>
                    <div className="text-xs text-forest/65 mt-0.5">{p.owner_name}</div>
                  </Link>
                ))}
                {providers.filter(matchesP).length === 0 && (
                  <div className="text-sm text-forest/50 italic">No providers.</div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
}
